'use client'

import React, { useCallback, useEffect, useRef } from 'react'

import { getPreferredVideoSources, getVideoPosterSrc } from '@/lib/videoSources'

export interface HeroBannerVideoModalProps {
  isOpen: boolean
  onClose: () => void
  videoSrc?: string
}

const DEFAULT_VIDEO_SRC = '/HomePageVideo.mp4'

export function HeroBannerVideoModal({
  isOpen,
  onClose,
  videoSrc = DEFAULT_VIDEO_SRC,
}: HeroBannerVideoModalProps) {
  const videoSources = getPreferredVideoSources(videoSrc)
  const posterSrc = getVideoPosterSrc(videoSrc)
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const modalHistoryPushedRef = useRef(false)
  const skipHistoryBackOnCloseRef = useRef(false)

  const closeModal = useCallback(() => {
    const shouldConsumeModalHistory =
      modalHistoryPushedRef.current &&
      !skipHistoryBackOnCloseRef.current &&
      typeof window !== 'undefined' &&
      Boolean(window.history.state?.heroVideoModal)

    onClose()

    if (shouldConsumeModalHistory) {
      window.history.back()
    }
  }, [onClose])

  // Focus close button when modal opens; restore focus on close
  useEffect(() => {
    if (!isOpen) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    closeButtonRef.current?.focus()
    return () => {
      previouslyFocused?.focus()
    }
  }, [isOpen])

  // Escape to close and body scroll lock (no scroll when video is playing)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    if (isOpen) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.left = '0'
      document.body.style.right = '0'
      document.body.style.overflow = 'hidden'
      window.addEventListener('keydown', handleEscape)
    }
    return () => {
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.overflow = ''
      if (scrollY) {
        window.scrollTo(0, Math.abs(parseInt(scrollY, 10)))
      }
      window.removeEventListener('keydown', handleEscape)
    }
  }, [closeModal, isOpen])

  // Push a temporary history entry so mobile hardware back closes the modal first.
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return

    if (!modalHistoryPushedRef.current) {
      window.history.pushState({ ...window.history.state, heroVideoModal: true }, '')
      modalHistoryPushedRef.current = true
    }

    const handlePopState = () => {
      if (!modalHistoryPushedRef.current) return
      skipHistoryBackOnCloseRef.current = true
      modalHistoryPushedRef.current = false
      closeModal()
      skipHistoryBackOnCloseRef.current = false
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      modalHistoryPushedRef.current = false
      skipHistoryBackOnCloseRef.current = false
    }
  }, [closeModal, isOpen])

  // Focus trap: keep Tab within modal
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab' || !dialogRef.current) return
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      const list = Array.from(focusables)
      const first = list[0]
      const last = list[list.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last?.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first?.focus()
        }
      }
    },
    [],
  )

  if (!isOpen) return null

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Video modal"
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/80 p-4"
      onKeyDown={handleKeyDown}
    >
      {/* Overlay click to close */}
      <button
        type="button"
        aria-label="Close modal"
        className="absolute inset-0 cursor-default"
        onClick={closeModal}
        tabIndex={-1}
      />
      {/* Video wrapper: same size as video so close button sits inside the video frame */}
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-4xl items-center justify-center">
        <div className="relative inline-block max-h-[90vh] w-full max-w-full">
          <video
            controls
            autoPlay
            playsInline
            preload="metadata"
            poster={posterSrc}
            className="max-h-[90vh] w-full max-w-full rounded object-contain shadow-2xl"
            aria-label="Video player"
          >
            {videoSources.map((source) => (
              <source key={source.src} src={source.src} type={source.type} />
            ))}
          </video>
          {/* Close button – top-right corner of video/content */}
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Close video"
            onClick={closeModal}
            className="absolute right-0 top-0 z-20 flex h-11 min-h-[44px] min-w-[44px] shrink-0 items-center justify-center bg-transparent text-white transition-opacity hover:opacity-80 focus:outline-none focus:opacity-80"
          >
            <svg
              aria-hidden
              className="h-5 w-5 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
